import { API_BASE_URL } from '@synergy/config';
import { Box, Theme } from '@mui/material';
import { ConfirmationDialog, PoolFormattedMessage, PoolTypography } from '@synergy/components';
import { DeleteIcon } from '@synergy/theme-next';
import { ExtranetIcon, ShoppingCartIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import urljoin from 'url-join';

import * as ContactPermissionActions from '../../permissions/store/contact-detail-permissions-actions';
import { contactPermissionsImpersonateLinksSelector } from '../store/contact-detail-permissions-selectors';
import { contactSelector } from '../../overview/store/contact-detail-overview-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    headerWithToggle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(3),
    },
    link: {
        display: 'flex',
        textDecoration: 'none',
        alignItems: 'center',
        color: theme.palette.text.primary,
        cursor: 'pointer',
        stroke: theme.palette.text.primary,

        '&:hover, &:focus': {
            color: theme.palette.primary.main,
            stroke: theme.palette.primary.main,
        },
    },
    icon: {
        marginRight: theme.spacing(1),
        width: 20,
        height: 20,
    },
}));

export const ContactDetailPermissionsImpersonateHeader = () => {
    const classes = useStyles();
    const dispatch = useThunkDispatch();

    const { shopSwitchUserUrl, supplierSwitchUserUrl, hasUser } = useAppSelector(
        contactPermissionsImpersonateLinksSelector
    );
    const contact = useAppSelector(contactSelector);
    const [showUserloginDeleteModal, setShowUserloginDeleteModal] = useState(false);

    const handleDeleteUserlogin = () => {
        dispatch(ContactPermissionActions.disableLogin());
        setShowUserloginDeleteModal(false);
    };

    return (
        <>
            <PoolTypography styleType="sectionTitle" component="div" className={classes.headerWithToggle}>
                <FormattedMessage id="app.contactsAndOrganizations.contact.credentials" />
                <div className={classes.row}>
                    {hasUser && (
                        <Box className={classes.link} onClick={() => setShowUserloginDeleteModal(true)}>
                            <DeleteIcon className={classes.icon} htmlColor="inherit" />
                            <PoolTypography
                                typography="body2"
                                id="app.contactsAndOrganizations.contact.removeLogin"
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>
                    )}
                    {(shopSwitchUserUrl || supplierSwitchUserUrl) && (
                        <PoolTypography
                            typography="body2"
                            id="app.contactsAndOrganizations.contact.impersonate"
                            sx={{ fontWeight: 600 }}
                        />
                    )}

                    {shopSwitchUserUrl && (
                        // eslint-disable-next-line react/jsx-no-target-blank
                        <a
                            href={urljoin(API_BASE_URL, shopSwitchUserUrl)}
                            target="_blank"
                            rel="noopener"
                            className={classes.link}
                        >
                            <ShoppingCartIcon className={classes.icon} htmlColor="inherit" />
                            <PoolTypography
                                typography="body2"
                                id="app.contactsAndOrganizations.contact.impersonateShop"
                            />
                        </a>
                    )}

                    {supplierSwitchUserUrl && (
                        // eslint-disable-next-line react/jsx-no-target-blank
                        <a
                            href={urljoin(API_BASE_URL, supplierSwitchUserUrl)}
                            target="_blank"
                            rel="noopener"
                            className={classes.link}
                        >
                            <ExtranetIcon className={classes.icon} htmlColor="inherit" />
                            <PoolTypography
                                typography="body2"
                                id="app.contactsAndOrganizations.contact.impersonateSupplier"
                            />
                        </a>
                    )}
                </div>
            </PoolTypography>

            <ConfirmationDialog
                isVisible={showUserloginDeleteModal}
                title={<FormattedMessage id="app.contactsAndOrganizations.contact.removeLogin" />}
                onCancel={() => setShowUserloginDeleteModal(false)}
                onConfirm={handleDeleteUserlogin}
                action={<FormattedMessage id="app.common.delete" />}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.contact.removeLoginText"
                    values={{ name: `${contact?.firstName} ${contact?.lastName}` }}
                />
            </ConfirmationDialog>
        </>
    );
};
